import { useMemo, useState } from "react"
import { Plus, Pencil, Boxes } from "lucide-react"
import {
  useGetItemGroupsQuery,
  useCreateItemGroupMutation,
  useUpdateItemGroupMutation,
} from "shared/redux/endpoints/procurementItems"
import { useGetActiveBudgetCategoriesQuery } from "shared/redux/endpoints/procurementV2"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { getApiErrorMessage } from "../../orderSubmitFeedback"
import { RULES, validateFields, hasErrors } from "../../fieldRules"
import { Card, Button, Checkbox, DataTable, EmptyState, Input, Select, Textarea } from "../../ui"
import { StatusPill } from "procurement/design"

const L_CODE_KEYS = Array.from({ length: 10 }, (_, i) => `l${i + 1}Code`)

const emptyForm = {
  id: null,
  code: "",
  name: "",
  categoryId: "",
  isActive: true,
  accountCode: "",
  notes: "",
  ...Object.fromEntries(L_CODE_KEYS.map((k) => [k, ""])),
}

// §2.2 Item Group — simple CRUD: list + form (code, name, parent category,
// active). Inline create/edit panel instead of a modal, matching the rest of
// the requester/manager design kit (no modal primitive in ./ui).
const ItemGroupsView = () => {
  const [editing, setEditing] = useState(null) // null = closed, emptyForm-shaped object otherwise
  const [errors, setErrors] = useState({})
  const { data: categories } = useGetActiveBudgetCategoriesQuery({ isActive: true })
  const { data: groups, isFetching } = useGetItemGroupsQuery()
  const [createItemGroup, { isLoading: creating }] = useCreateItemGroupMutation()
  const [updateItemGroup, { isLoading: updating }] = useUpdateItemGroupMutation()
  const busy = creating || updating

  const categoryOptions = useMemo(
    () => (categories || []).map((c) => ({ value: c.id, label: c.name })),
    [categories]
  )
  const categoryName = (id) => categoryOptions.find((c) => String(c.value) === String(id))?.label || "—"

  const openCreate = () => {
    setErrors({})
    setEditing({ ...emptyForm })
  }
  const openEdit = (g) => {
    setErrors({})
    setEditing({
      id: g.id,
      code: g.code || "",
      name: g.name || "",
      categoryId: g.categoryId || "",
      isActive: g.isActive ?? true,
      accountCode: g.accountCode || "",
      notes: g.notes || "",
      ...Object.fromEntries(L_CODE_KEYS.map((k) => [k, g[k] || ""])),
    })
  }

  const setField = (k, v) => {
    setEditing((p) => ({ ...p, [k]: v }))
    setErrors((p) => ({ ...p, [k]: null }))
  }

  const handleSave = async () => {
    if (!editing?.code.trim() || !editing?.name.trim() || !editing?.categoryId) return
    const errs = validateFields(editing, RULES.itemGroup)
    setErrors(errs)
    if (hasErrors(errs)) return
    const body = {
      code: editing.code.trim(),
      name: editing.name.trim(),
      categoryId: editing.categoryId,
      isActive: !!editing.isActive,
      accountCode: editing.accountCode.trim() || undefined,
      notes: editing.notes.trim() || undefined,
      ...Object.fromEntries(L_CODE_KEYS.map((k) => [k, editing[k]?.trim() || undefined])),
    }
    try {
      if (editing.id) {
        await updateItemGroup({ id: editing.id, ...body }).unwrap()
        showToastSuccess("Item group updated.")
      } else {
        await createItemGroup(body).unwrap()
        showToastSuccess("Item group created.")
      }
      setEditing(null)
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Couldn't save this item group. Please try again."))
    }
  }

  const columns = useMemo(
    () => [
      { key: "code", header: "Code", strong: true, sortable: true },
      { key: "name", header: "Name", sortable: true },
      { key: "category", header: "Category", render: (r) => r.categoryName || categoryName(r.categoryId) },
      { key: "accountCode", header: "Account Code", render: (r) => r.accountCode || "—" },
      {
        key: "isActive",
        header: "Active",
        render: (r) => <StatusPill status={r.isActive ? "active" : "inactive"} />,
      },
      {
        key: "actions",
        header: "",
        render: (r) => (
          <Button variant="ghost" size="sm" leftIcon={<Pencil aria-hidden />} onClick={() => openEdit(r)}>
            Edit
          </Button>
        ),
      },
    ],
    [categoryOptions]
  )

  return (
    <>
      {editing && (
        <Card icon={<Boxes aria-hidden />} title={editing.id ? "Edit Item Group" : "New Item Group"}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 16,
            }}
          >
            <Input
              label="Code"
              required
              value={editing.code}
              error={errors.code}
              onChange={(e) => setField("code", e.target.value)}
              disabled={busy}
            />
            <Input
              label="Name"
              required
              value={editing.name}
              error={errors.name}
              onChange={(e) => setField("name", e.target.value)}
              disabled={busy}
            />
            <Select
              label="Category"
              required
              value={editing.categoryId}
              onChange={(v) => setField("categoryId", v)}
              options={categoryOptions}
              disabled={busy}
            />
            <Input
              label="Account Code"
              value={editing.accountCode}
              error={errors.accountCode}
              onChange={(e) => setField("accountCode", e.target.value)}
              disabled={busy}
            />
            <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: 10 }}>
              <Checkbox
                checked={editing.isActive}
                onChange={(v) => setField("isActive", v)}
                label="Active"
              />
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--pc-body)", marginBottom: 6 }}>
              L Codes
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(80px, 1fr))",
                gap: 10,
              }}
            >
              {L_CODE_KEYS.map((key, i) => (
                <Input
                  key={key}
                  label={`L${i + 1}`}
                  value={editing[key]}
                  error={errors[key]}
                  onChange={(e) => setField(key, e.target.value)}
                  disabled={busy}
                />
              ))}
            </div>
          </div>

          <div style={{ marginTop: 16 }}>
            <Textarea
              label="Notes"
              value={editing.notes}
              onChange={(e) => setField("notes", e.target.value)}
              disabled={busy}
            />
          </div>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
            <Button variant="secondary" onClick={() => setEditing(null)} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={busy || !editing.code.trim() || !editing.name.trim() || !editing.categoryId}
              onClick={handleSave}
            >
              {busy ? "Saving…" : "Save"}
            </Button>
          </div>
        </Card>
      )}

      <Card
        flush
        action={
          <Button variant="secondary" size="sm" leftIcon={<Plus aria-hidden />} onClick={openCreate}>
            New group
          </Button>
        }
      >
        <DataTable
          columns={columns}
          rows={groups || []}
          isLoading={isFetching}
          initialSort={{ key: "code", dir: "asc" }}
          empty={
            <EmptyState
              icon={<Boxes aria-hidden />}
              title="No item groups yet"
              text="Create a group to start organizing the item catalog."
              action={
                <Button variant="primary" leftIcon={<Plus aria-hidden />} onClick={openCreate}>
                  New group
                </Button>
              }
            />
          }
        />
      </Card>
    </>
  )
}

export default ItemGroupsView
